import { SortDirection } from '@/api/types';
import { getTableMinWidth, TableColumn, TableRow, TextCell } from '@/design-system';
import { CryptoAsset } from '@/shared/modules/wallet/use-crypto-assets';

import { SelectableTable } from '../assets-table.styled';
import { PerformancePositionSelectionIcon } from '../performance-position-selection-icon';
import { CoinIcon } from './coin-icon';
import { NameCell } from './crypto-assets-table.styled';
import { fmtAmount, fmtUsd } from './formatters';
import { RowActions } from './row-actions';
import { CryptoPerformanceSelection, SortDir, SortKey } from './types';

interface CryptoAssetTableRow extends TableRow {
  coin: CryptoAsset;
  active: boolean;
}

type CryptoAssetsTableProps = {
  assets: CryptoAsset[];
  sortedAssets: CryptoAsset[];
  sortKey: SortKey;
  sortDir: SortDir;
  activePositionId?: string;
  suiAddress?: string;
  onSort: (_key: SortKey) => void;
  onSelectPosition: (_selection: CryptoPerformanceSelection | null) => void;
};

export const CryptoAssetsTable = ({
  assets,
  sortedAssets,
  sortKey,
  sortDir,
  activePositionId,
  suiAddress,
  onSort,
  onSelectPosition,
}: CryptoAssetsTableProps) => {
  const columns: TableColumn<CryptoAssetTableRow>[] = [
    {
      key: 'symbol',
      header: 'Asset Name',
      sortable: true,
      minWidth: 220,
      cell: ({ coin, active }) => (
        <NameCell>
          {coin.positionId ? (
            <PerformancePositionSelectionIcon
              active={active}
              dimmed={Boolean(activePositionId) && !active}
            />
          ) : null}
          <CoinIcon symbol={coin.symbol} iconUrl={coin.iconUrl} />
          <TextCell title={coin.symbol} />
        </NameCell>
      ),
    },
    {
      key: 'balance',
      header: 'Amount',
      sortable: true,
      width: '220px',
      cell: ({ coin }) => <TextCell title={fmtAmount(coin.balance)} />,
    },
    {
      key: 'costBasis',
      header: 'Cost Basis',
      width: '220px',
      cell: ({ coin }) => <TextCell title={fmtUsd(coin.costBasisInUsd)} />,
    },
    {
      key: 'usdValue',
      header: 'Present Value',
      sortable: true,
      width: '220px',
      cell: ({ coin }) => <TextCell title={fmtUsd(coin.usdValue)} />,
    },
    {
      key: 'actions',
      header: 'Actions',
      width: '208px',
      align: 'right',
      pinned: true,
      cell: ({ coin }) => <RowActions coin={coin} assets={assets} suiAddress={suiAddress} />,
    },
  ];

  const rows: CryptoAssetTableRow[] = sortedAssets.map((coin) => {
    const active = coin.positionId === activePositionId;
    return {
      key: coin.coinType,
      coin,
      active,
      onClick: coin.positionId
        ? () => onSelectPosition(active ? null : { id: coin.positionId!, name: coin.symbol })
        : undefined,
    };
  });

  return (
    <SelectableTable
      columns={columns}
      rows={rows}
      size="lg"
      sortKey={sortKey}
      sortDirection={sortDir === 'asc' ? SortDirection.ASC : SortDirection.DESC}
      onSort={(key) => onSort(key as SortKey)}
      minTableWidth={getTableMinWidth(columns)}
    />
  );
};
